import React, { useState, useEffect } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import DirectorDashboard from './Director';
import PressEfficiencyPage from './PressEfficiency';
import PressTimePage from './PressTime';
import BranchCompare from './BranchCompare';
import BranchSwitch from '../components/BranchSwitch';
import { sheetAPI } from '../api';

// ─── Дашборд директора: один экран вместо трёх ───────────────────────────────
//
// Было три пункта меню — «Обзор», «Дашборд директора» и «Эффективность
// прессов» — отвечающих на пересекающиеся вопросы. Директор заходит раз в день
// и хочет увидеть картину, а не выбирать из трёх дверей, за которыми примерно
// одно и то же.
//
// «Сводка» убрана: она повторяла выпуск, OEE, заказы и склад из «Разбора», а
// брак показывала нулём — он считался по полю, которым в цеху не пользуются.
//
// ── Этажей стало два вместо трёх ────────────────────────────────────────────
//
// Меню вело в «📊 Разбор по цифрам», и только ТАМ, полосой кнопок поверх
// содержимого, открывались настоящие семь разделов. То есть до «Прогноза» было
// три нажатия, и два первых ничего не называли: с бокового меню директор видел
// два слова вместо восьми и не знал, что за ними лежит.
//
// Теперь все восемь стоят прямо в боковом меню, сгруппированные по вопросу, на
// который отвечают. Полоса кнопок из Director.js убрана — иначе одно и то же
// выбиралось бы в двух местах.
//
// Названия правлены там, где по ним открывали не то:
//   «Прессы (OEE)»  → «Загрузка прессов»   (рядом стояло «Время прессов»)
//   «Время прессов» → «Простои прессов»    (оно про остановки, а не про часы)
//   «Пересортица»   → «Кто менял сорта»    (обе шли со значком 🏷 …)
//   «Сорта»         → «Брак по сортам»     (… и различить их было нечем)
//   «Режим»         → «Режим и брак»       (это связь режима с браком)
// Счётчик заявок на пересортицу горит прямо в меню: пока директор не решил,
// листы заморожены — ни в паллету, ни в отгрузку. Заявка не должна лежать
// сутками, поэтому цифру видно с любого раздела.
const buildSections = (regradeCount) => [
  { title: 'Сейчас', items: [
    { key: 'orders',   label: '📋 Заказы' },
  ]},
  { title: 'Производство', items: [
    { key: 'output',   label: '🏭 Выпуск' },
    { key: 'presses',  label: '⚙️ Загрузка прессов' },
    { key: 'time',     label: '⏱ Простои прессов' },
  ]},
  { title: 'Качество', items: [
    { key: 'regrade',  label: '🏷 Кто менял сорта',
      badge: regradeCount, alert: regradeCount > 0 },
    { key: 'defects',  label: '📉 Брак по сортам' },
    { key: 'mode',     label: '🌡 Режим и брак' },
  ]},
  { title: 'Анализ', items: [
    { key: 'forecast', label: '📈 Прогноз' },
  ]},
];

// Разделы, которые рисует сам дашборд. Остальные — отдельные страницы.
const DEEP = ['orders', 'output', 'presses', 'regrade', 'defects', 'mode', 'forecast'];

export default function DirectorHub() {
  const [view, setView] = useState('orders');
  // Как смотрим на завод: один цех / все разом / цех к цеху.
  //
  // Отдельного дашборда для «нескольких цехов» не делаем: два дашборда значат,
  // что одни и те же числа считаются в двух местах, и однажды они разойдутся.
  // Полоса переключает то, ЧТО подставляется в те же самые запросы.
  const [mode, setMode] = useState('one');

  // Считаем здесь, а не в дашборде: дашборд размонтируется, когда директор
  // уходит в «Простои прессов», и цифра пропадала бы вместе с ним.
  const [regradeCount, setRegradeCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const pull = () => sheetAPI.gradeRequests({ status: 'pending', limit: 1 })
      .then(r => { if (alive) setRegradeCount(r.data.pending || 0); })
      .catch(() => {});
    pull();
    const id = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <Layout title={t('Дашборд директора')}
            sections={buildSections(regradeCount)} section={view} onSection={setView}>
      {/* Полосы нет вовсе, пока цех один: выбор из одного выглядит как забытая
          настройка и заставляет искать несуществующее. */}
      <BranchSwitch mode={mode} onMode={setMode} />
      {mode === 'compare' && <BranchCompare days={7} />}

      {mode !== 'compare' && DEEP.includes(view) && (
        <DirectorDashboard bare section={view} onSection={setView} onOpen={setView}
                           onRegradeCount={setRegradeCount} />
      )}

      {mode !== 'compare' && view === 'time' && <PressTimePage bare />}

      {/* «Прессы по отдельности» в меню слева нет: путь сюда один — кнопка
          «Подробно по каждому прессу» из «Загрузки прессов». Значит и выход
          отсюда должны дать мы, иначе человек нажимает кнопку и оказывается в
          тупике: активного пункта в меню не подсвечено, возврата нет.

          Возвращаем ровно туда, откуда пришли. */}
      {mode !== 'compare' && view === 'pressdetail' && (
        <PressEfficiencyPage bare onBack={() => setView('presses')} />
      )}
    </Layout>
  );
}
